import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import {
  IncentivesWizardEntities,
  IncentivesWizardModel,
  IncentivesWizardPaths,
} from 'client/data/models/incentives-wizard';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import {
  StepProps,
  StepPropsDefaults,
} from 'site-modules/shared/components/incentives/incentives-wizard/prop-types/step-props';

import { BaseQuestionStep } from 'site-modules/shared/components/incentives/incentives-wizard/steps/base-question-step';
import { useQuestionTracking } from 'site-modules/shared/components/incentives/incentives-wizard/hooks/use-question-tracking';
import { WizardZipForm } from 'site-modules/shared/components/incentives/incentives-wizard/steps/components/wizard-zip-form';

export const ZipInputWizardQuestionUI = memo(
  ({
    stepConfig,
    stepMeta = StepPropsDefaults.stepMeta,
    answers = StepPropsDefaults.answers,
    eligibleIncentiveIds = StepPropsDefaults.eligibleIncentiveIds,
    incentives = StepPropsDefaults.incentives,
    allIncentives,
    onAnswer = StepPropsDefaults.onAnswer,
    onBack = StepPropsDefaults.onBack,
    context = StepPropsDefaults.context,
    flowSteps = StepPropsDefaults.flowSteps,
    wizardFilters,
    creativeId = StepPropsDefaults.creativeId,
    isInternational = false,
  }) => {
    const filtersZipCode = get(wizardFilters, 'incentiveFilters.zip', '');
    const [handleAnswerTracking] = useQuestionTracking({ stepConfig, creativeId, stepMeta });

    const handleAnswer = useCallback(
      ({ value, didChange }) => {
        onAnswer([value, { isChanged: didChange }]);
        handleAnswerTracking(value);
      },
      [handleAnswerTracking, onAnswer]
    );

    const zipFromAnswers = answers[stepConfig.id]?.[0];

    return (
      <BaseQuestionStep
        stepConfig={stepConfig}
        stepMeta={stepMeta}
        answers={answers}
        eligibleIncentiveIds={eligibleIncentiveIds}
        incentives={incentives}
        allIncentives={allIncentives}
        onAnswer={onAnswer}
        onBack={onBack}
        context={context}
        flowSteps={flowSteps}
      >
        <div className="d-flex justify-content-center mb-2">
          <WizardZipForm
            isInternational={isInternational}
            zipCode={filtersZipCode}
            zipFromAnswers={zipFromAnswers}
            onAnswer={handleAnswer}
          />
        </div>
      </BaseQuestionStep>
    );
  }
);

ZipInputWizardQuestionUI.displayName = 'ZipInputWizardQuestion';

ZipInputWizardQuestionUI.propTypes = {
  ...StepProps,
  wizardFilters: IncentivesWizardEntities.IncentivesWizardFilters.isRequired,
  isInternational: PropTypes.bool,
};

export const stateToPropsConfig = {
  wizardFilters: bindToPath(IncentivesWizardPaths.getIncentivesWizardFilters(), IncentivesWizardModel, null, false),
  isInternational: bindToPath('location', VisitorModel, visitorLocation => isUnknownZip(visitorLocation)),
};

export const ZipInputWizardQuestion = connectToModel(ZipInputWizardQuestionUI, stateToPropsConfig);
